import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import UserCard from "../components/UserCard";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Video, Loader2, X, Sparkles } from "lucide-react";

const MAP_BG = "https://images.unsplash.com/photo-1706823871410-ed8b01faef7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx3b3JsZCUyMG1hcCUyMGFic3RyYWN0fGVufDB8fHx8MTc4MzMwMTM5Nnww&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [partners, setPartners] = useState([]);
  const [rel, setRel] = useState({});
  const [searching, setSearching] = useState(false);
  const pollRef = useRef(null);

  const loadRelations = async () => {
    const { data } = await api.get("/friends");
    const m = {};
    data.friends.forEach((f) => (m[f.id] = "friend"));
    data.outgoing.forEach((f) => (m[f.id] = "pending"));
    data.incoming.forEach((f) => (m[f.id] = "pending"));
    setRel(m);
  };

  useEffect(() => {
    api.get("/match/partners").then((r) => setPartners(r.data.partners)).catch(() => {});
    loadRelations();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; loadRelations/pollRef are stable
  }, []);

  const startMatch = async () => {
    setSearching(true);
    const poll = async () => {
      try {
        const { data } = await api.post("/match/find");
        if (data.matched) {
          clearInterval(pollRef.current);
          setSearching(false);
          nav(`/room/${data.room_id}`, { state: { isCaller: data.is_caller, partner: data.partner } });
        }
      } catch (e) { console.error("match poll error", e); }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
  };

  const cancelMatch = async () => {
    clearInterval(pollRef.current);
    await api.post("/match/cancel").catch(() => {});
    setSearching(false);
  };

  const addFriend = async (u) => {
    await api.post(`/friends/request/${u.id}`);
    setRel({ ...rel, [u.id]: "pending" });
    toast.success(`Friend request sent to ${u.name}`);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="relative overflow-hidden rounded-3xl bg-secondary/15 border border-border p-8 sm:p-12">
          <img src={MAP_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-primary bg-primary/10 rounded-full px-4 py-1.5">
              <Sparkles className="w-4 h-4" /> {t("dash.readyBadge")}
            </span>
            <h1 className="mt-5 text-3xl sm:text-4xl font-heading font-black tracking-tight">
              {t("dash.title")}
            </h1>
            <p className="mt-3 text-foreground/70 text-lg">
              {t("dash.sub")}
            </p>
            <Button size="lg" className="mt-7 rounded-full h-12 px-8 text-base" onClick={startMatch} data-testid="quick-match-btn">
              <Video className="w-5 h-5 mr-2" /> {t("dash.quickMatch")}
            </Button>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-heading font-bold tracking-tight">{t("dash.suggested")}</h2>
          <p className="text-foreground/70 mt-1">{t("dash.suggestedSub")}</p>
          {partners.length === 0 ? (
            <p className="text-muted-foreground mt-8" data-testid="no-partners">{t("dash.noPartners")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {partners.map((u) => (
                <UserCard key={u.id} u={u} actionState={rel[u.id]} onAction={addFriend} showStatus={true} testid={`partner-${u.id}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={searching} onOpenChange={(o) => { if (!o) cancelMatch(); }}>
        <DialogContent className="rounded-3xl max-w-sm text-center" data-testid="searching-dialog">
          <div className="flex flex-col items-center py-6">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <span className="relative w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                <Video className="w-7 h-7" />
              </span>
            </div>
            <h3 className="mt-6 font-heading font-bold text-xl">{t("dash.finding")}</h3>
            <p className="text-foreground/70 mt-1 text-sm">{t("dash.findingSub")}</p>
            <Loader2 className="w-5 h-5 animate-spin text-primary mt-4" />
            <Button variant="ghost" className="rounded-full mt-4" onClick={cancelMatch} data-testid="cancel-match-btn">
              <X className="w-4 h-4 mr-1" /> {t("dash.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
